import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './OpsTasksPrintPreview.module.scss';
import { OpsTask } from 'model/OpsTask';
import OpsTasksPrintTable, { OpsTasksPrintLocationGroup } from '../OpsTasksPrintTable/OpsTasksPrintTable';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import Spinner from 'components/UI/V3/Spinner';

export interface OpsTasksPrintPreviewProps {
    tasks: OpsTask[];
    dateLabel: string;
    onClose: () => void;
}

function usePrint(onClose: () => void, isReadyToPrint: boolean) {
    useHideHubSpotChat(true);

    useEffect(() => {
        if (!isReadyToPrint) return;

        document.body.classList.add('printing');

        const timer = setTimeout(() => window.print(), 50);

        const handleAfterPrint = () => {
            document.body.classList.remove('printing');
            onClose();
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
            document.body.classList.remove('printing');
        };
    }, [onClose, isReadyToPrint]);

    useHideHubSpotChat(false);
}

export const OpsTasksPrintPreview: React.FC<OpsTasksPrintPreviewProps> = ({ tasks, dateLabel, onClose }) => {
    const [isReadyToPrint, setIsReadyToPrint] = useState(false);

    usePrint(onClose, isReadyToPrint);

    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const marketplaceNamesById = useMemo(
        () => new Map(organizationMarketplaces.map(m => [m.id, m.name] as const)),
        [organizationMarketplaces]
    );

    const groups = useMemo<OpsTasksPrintLocationGroup[]>(() => {
        const map = new Map<number, OpsTask[]>();
        for (const task of tasks) {
            const group = map.get(task.marketplaceId) ?? [];
            group.push(task);
            map.set(task.marketplaceId, group);
        }
        return Array.from(map.entries()).map(([id, groupTasks]) => ({
            locationName: marketplaceNamesById.get(id) ?? String(id),
            tasks: groupTasks
        }));
    }, [tasks, marketplaceNamesById]);

    return ReactDOM.createPortal(
        <div className={styles.printOverlay}>
            <div className={styles.printContent}>
                {!isReadyToPrint ? (
                    <div className={styles.spinnerOverlay}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className={styles.printHeader}>
                            <span className={styles.title}>Task List · </span>
                            <span className={styles.date}>{dateLabel}</span>
                        </div>
                        <OpsTasksPrintTable groups={groups} />
                    </>
                )}
            </div>
            <div className={styles.printFooter}>
                <img
                    className={styles.footerLogo}
                    src="/images/logo/poweredByCuddles.png"
                    onLoad={() => setIsReadyToPrint(true)}
                />
            </div>
        </div>,
        document.body
    );
};

export default OpsTasksPrintPreview;
